import { useState, useCallback } from "react";
import { isSearchInFlight } from "@/types/api";
import { Search as SearchIcon, Plug, SlidersHorizontal } from "lucide-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SearchBar } from "@/components/investigation/search-bar";
import { SearchResults } from "@/components/investigation/search-results";
import { EntityTimeline } from "@/components/investigation/entity-timeline";
import { SavedSearches } from "@/components/investigation/saved-searches";
import { Badge } from "@/components/ui/badge";
import {
  useStartSearch,
  useSearchStatus,
  useHasIngestedData,
  useTranslateInvestigationQuery,
} from "@/hooks/use-api";
import type { NaturalLanguageTranslation, SearchRequest, SearchResult } from "@/types/api";
import { APP_ROUTES, appRouteForPathname } from "@/lib/app-routes";

type SearchPhase =
  | { phase: "idle" }
  | { phase: "submitting" }
  | { phase: "polling"; executionId: string }
  | { phase: "succeeded"; executionId: string; results: SearchResult }
  | { phase: "failed"; error: string };

export default function InvestigationPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") ?? "";
  const [searchState, setSearchState] = useState<SearchPhase>({
    phase: "idle",
  });
  const [currentQuery, setCurrentQuery] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [entityTarget, setEntityTarget] = useState<{
    type: string;
    id: string;
  } | null>(null);
  const [translation, setTranslation] = useState<NaturalLanguageTranslation | null>(null);

  const startSearch = useStartSearch();
  const translateSearch = useTranslateInvestigationQuery();
  const { hasData, isLoading: summaryLoading, isError: summaryError } = useHasIngestedData();

  const executionId =
    searchState.phase === "polling" || searchState.phase === "succeeded"
      ? searchState.executionId
      : null;

  const searchStatus = useSearchStatus(
    searchState.phase === "polling" ? executionId : null,
  );

  // Watch for search completion
  if (
    searchState.phase === "polling" &&
    searchStatus.data &&
    !isSearchInFlight(searchStatus.data.status)
  ) {
    if (searchStatus.data.status === "SUCCEEDED") {
      setSearchState({
        phase: "succeeded",
        executionId: searchState.executionId,
        results: searchStatus.data,
      });
    } else {
      setSearchState({
        phase: "failed",
        error: searchStatus.data.error ?? "Query failed",
      });
    }
  }

  const handleSearch = useCallback(
    async (query: string, timeStart: string, timeEnd: string) => {
      setSearchState({ phase: "submitting" });
      setTranslation(null);

      const normalizedQuery = query.trim();
      const nextSearchParams = new URLSearchParams(searchParams);
      if (normalizedQuery) nextSearchParams.set("q", normalizedQuery);
      else nextSearchParams.delete("q");
      setSearchParams(nextSearchParams, { replace: true });

      try {
        let searchReq: SearchRequest;
        if (normalizedQuery) {
          const translated = await translateSearch.mutateAsync({
            query: normalizedQuery,
            time_start: timeStart,
            time_end: timeEnd,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          });
          setTranslation(translated);
          setCurrentQuery({ natural_language: translated.query, ...translated.search });
          searchReq = translated.search;
        } else {
          // Pre-translator saved searches may carry only a time window ("all
          // events in the window"); run them without a translate round-trip.
          searchReq = { time_start: timeStart, time_end: timeEnd };
          setCurrentQuery({ ...searchReq });
        }
        const result = await startSearch.mutateAsync(searchReq);
        if (result.execution_id === null) {
          setSearchState({
            phase: "succeeded",
            executionId: "",
            results: { status: "SUCCEEDED", results: [] },
          });
          return;
        }
        setSearchState({
          phase: "polling",
          executionId: result.execution_id,
        });
      } catch (err) {
        setSearchState({
          phase: "failed",
          error: err instanceof Error ? err.message : "Search failed",
        });
      }
    },
    [searchParams, setSearchParams, startSearch, translateSearch],
  );

  function handleLoadSearch(queryJson: Record<string, unknown>) {
    // Older saved searches stored the keyword under free_text or query.
    const naturalLanguage = queryJson.natural_language as string | undefined;
    const query = naturalLanguage ?? (queryJson.free_text as string) ?? (queryJson.query as string) ?? "";
    const now = new Date();
    // Natural-language time phrases (for example "yesterday") are resolved
    // again when re-run. Queries without one retain the saved absolute window.
    const timeStart =
      (queryJson.time_start as string) ?? new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const timeEnd = (queryJson.time_end as string) ?? now.toISOString();
    handleSearch(query, timeStart, timeEnd);
  }

  function handleEntityClick(entityType: string, entityId: string) {
    setEntityTarget({ type: entityType, id: entityId });
  }

  const searchResult =
    searchState.phase === "succeeded" ? searchState.results : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SearchIcon className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Investigation
          </h1>
          <p className="text-sm text-muted-foreground">
            Find security events using plain English
          </p>
        </div>
      </div>

      <SearchBar
        onSearch={handleSearch}
        isSubmitting={searchState.phase === "submitting"}
        initialQuery={queryParam}
      />

      {translation && (
        <div
          className="rounded-lg border bg-muted/30 p-4"
          aria-label={
            translation.mode === "aggregate"
              ? "Interpreted aggregate query"
              : "Interpreted search filters"
          }
        >
          <div className="mb-2 flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">
              {translation.mode === "aggregate"
                ? "Interpreted aggregate query"
                : "Interpreted as exact event filters"}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {translation.interpretation.map((item) => (
              <Badge key={item} variant="secondary">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <SavedSearches
        currentQuery={
          searchState.phase === "succeeded" ? currentQuery : null
        }
        onLoadSearch={handleLoadSearch}
      />

      {searchState.phase === "failed" && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {searchState.error}
        </div>
      )}

      {searchState.phase === "idle" && !hasData && !summaryLoading && !summaryError && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <div className="rounded-full bg-muted p-4">
            <Plug className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-sm font-semibold">No data ingested yet</h3>
          <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
            Install an agent or connect a source to start investigating security events.
          </p>
          <Link
            to={appRouteForPathname(location.pathname, APP_ROUTES.integrations)}
            className={cn(buttonVariants({ variant: "outline" }), "mt-6")}
          >
            Set up an integration
          </Link>
        </div>
      )}

      {searchState.phase === "idle" && (hasData || summaryLoading || summaryError) && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <div className="rounded-full bg-muted p-3">
            <SearchIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-sm font-semibold">Run a search</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Ask for specific events, such as failed logins from an IP yesterday
          </p>
          <Link
            to="/docs/guides/search-investigation"
            className="mt-2 text-sm text-primary underline"
          >
            See the search guide
          </Link>
        </div>
      )}

      <SearchResults
        result={searchResult}
        isPolling={searchState.phase === "polling"}
        isAggregate={translation?.mode === "aggregate"}
        onEntityClick={handleEntityClick}
      />

      <EntityTimeline
        entityType={entityTarget?.type ?? null}
        entityId={entityTarget?.id ?? null}
        onClose={() => setEntityTarget(null)}
      />
    </div>
  );
}
